import NProgress from 'nprogress';

const SHOW_DELAY_MS = 120;
const MIN_VISIBLE_MS = 220;
const WATCHDOG_MS = 6000;

let configured = false;
let historyObserverCleanup = null;

let navigationCounter = 0;
let activeNavigationId = 0;
let activeNavigationPath = null;
let navigationPending = false;
let barVisibleAt = 0;
let lastObservedPathname = typeof window !== 'undefined' ? window.location.pathname : '';

let showTimer = null;
let doneTimer = null;
let watchdogTimer = null;

const clearTimer = (timerId) => {
  if (timerId) {
    clearTimeout(timerId);
  }
};

const clearPendingTimers = () => {
  clearTimer(showTimer);
  clearTimer(doneTimer);
  clearTimer(watchdogTimer);
  showTimer = null;
  doneTimer = null;
  watchdogTimer = null;
};

const configure = () => {
  if (configured) return;
  NProgress.configure({
    showSpinner: false,
    trickleSpeed: 200,
    minimum: 0.08,
  });
  configured = true;
};

const getPathnameFromHistoryUrl = (nextUrl) => {
  if (typeof window === 'undefined') return '';
  if (nextUrl == null) return window.location.pathname;

  if (typeof nextUrl === 'string' || nextUrl instanceof URL) {
    try {
      return new URL(String(nextUrl), window.location.href).pathname;
    } catch {
      return window.location.pathname;
    }
  }

  if (typeof nextUrl === 'object' && typeof nextUrl.pathname === 'string') {
    return nextUrl.pathname;
  }

  return window.location.pathname;
};

const finalizeNavigation = (navigationId) => {
  if (navigationId !== activeNavigationId) return;
  clearTimer(doneTimer);
  doneTimer = null;
  navigationPending = false;
  activeNavigationPath = null;
  barVisibleAt = 0;
  if (NProgress.isStarted()) {
    NProgress.done();
  }
};

export const startRouteProgress = ({ fromPath, toPath, trigger = 'manual' } = {}) => {
  if (typeof window === 'undefined') return 0;
  configure();

  const from = fromPath || window.location.pathname;
  const to = toPath || from;
  if (!to || to === from) return activeNavigationId;

  clearPendingTimers();
  navigationCounter += 1;
  activeNavigationId = navigationCounter;
  activeNavigationPath = to;
  navigationPending = true;

  const navigationId = activeNavigationId;

  if (!NProgress.isStarted()) {
    showTimer = setTimeout(() => {
      if (!navigationPending || activeNavigationId !== navigationId) return;
      NProgress.start();
      barVisibleAt = Date.now();
    }, SHOW_DELAY_MS);
  }

  watchdogTimer = setTimeout(() => {
    if (!navigationPending || activeNavigationId !== navigationId) return;
    console.warn('[RouteProgress] Watchdog timeout, forcing completion', {
      navigationId,
      trigger,
      path: activeNavigationPath,
    });
    finalizeNavigation(navigationId);
  }, WATCHDOG_MS);

  return navigationId;
};

export const completeRouteProgress = ({ navigationId = activeNavigationId, reason = 'ready', force = false } = {}) => {
  if (typeof window === 'undefined') return false;
  configure();

  if (!force && navigationId !== activeNavigationId) return false;
  if (!navigationPending && !NProgress.isStarted()) return false;

  clearTimer(showTimer);
  clearTimer(watchdogTimer);
  showTimer = null;
  watchdogTimer = null;

  if (!NProgress.isStarted()) {
    if (navigationId === activeNavigationId) {
      navigationPending = false;
      activeNavigationPath = null;
    }
    return true;
  }

  const elapsedVisible = barVisibleAt ? Date.now() - barVisibleAt : MIN_VISIBLE_MS;
  const waitMs = force ? 0 : Math.max(0, MIN_VISIBLE_MS - elapsedVisible);

  clearTimer(doneTimer);
  if (waitMs === 0) {
    finalizeNavigation(navigationId);
  } else {
    doneTimer = setTimeout(() => {
      finalizeNavigation(navigationId);
    }, waitMs);
  }

  if (import.meta.env.DEV && reason === 'watchdog') {
    console.warn('[RouteProgress] Completed by watchdog', { navigationId });
  }

  return true;
};

export const getActiveRouteNavigationId = () => activeNavigationId;

export const handlePathnameCommit = (fromPath, toPath) => {
  if (typeof window === 'undefined') return;
  if (!toPath || fromPath === toPath) return;

  lastObservedPathname = toPath;

  if (!navigationPending) {
    startRouteProgress({ fromPath, toPath, trigger: 'route-fallback' });
    return;
  }

  // If a second navigation happened without history hooks observing it,
  // restart progress for the new destination.
  if (activeNavigationPath && activeNavigationPath !== toPath) {
    startRouteProgress({ fromPath, toPath, trigger: 'route-fallback-mismatch' });
  }
};

export const installRouteProgressObserver = () => {
  if (typeof window === 'undefined') return () => {};
  if (historyObserverCleanup) return historyObserverCleanup;

  configure();
  lastObservedPathname = window.location.pathname;

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function patchedPushState(...args) {
    const nextPath = getPathnameFromHistoryUrl(args[2]);
    const currentPath = window.location.pathname;
    if (nextPath && nextPath !== currentPath) {
      startRouteProgress({ fromPath: currentPath, toPath: nextPath, trigger: 'history.pushState' });
      lastObservedPathname = nextPath;
    }
    return originalPushState.apply(this, args);
  };

  window.history.replaceState = function patchedReplaceState(...args) {
    const nextPath = getPathnameFromHistoryUrl(args[2]);
    const currentPath = window.location.pathname;
    if (nextPath && nextPath !== currentPath) {
      startRouteProgress({ fromPath: currentPath, toPath: nextPath, trigger: 'history.replaceState' });
      lastObservedPathname = nextPath;
    }
    return originalReplaceState.apply(this, args);
  };

  const onPopState = () => {
    const nextPath = window.location.pathname;
    if (nextPath && nextPath !== lastObservedPathname) {
      startRouteProgress({
        fromPath: lastObservedPathname || nextPath,
        toPath: nextPath,
        trigger: 'history.popstate',
      });
      lastObservedPathname = nextPath;
    }
  };

  window.addEventListener('popstate', onPopState);

  historyObserverCleanup = () => {
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', onPopState);
    historyObserverCleanup = null;
  };

  return historyObserverCleanup;
};

